import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormGroup from '@helsenorge/designsystem-react/components/FormGroup';
import HighlightPanel from '@helsenorge/designsystem-react/components/HighlightPanel';
import Icon, { IconSize } from '@helsenorge/designsystem-react/components/Icon';
import PaperPlane from '@helsenorge/designsystem-react/components/Icons/PaperPlane';
import Label, { Sublabel } from '@helsenorge/designsystem-react/components/Label';
import NotificationPanel from '@helsenorge/designsystem-react/components/NotificationPanel';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import TextArea from '@helsenorge/designsystem-react/components/Textarea';
import Validation from '@helsenorge/designsystem-react/components/Validation';
import { debounce } from '@helsenorge/designsystem-react/utils/debounce';
import FeedbackButton from '@helsenorge/core-cms/feedbackbutton';
import { track } from '@helsenorge/framework-utils/amplitude';
import { GittTilbakemelding } from '@helsenorge/framework-utils/amplitude/ampli';
import { error as logError } from '@helsenorge/framework-utils/logger';
import ScoreInput from './score-input';
import { scoreValueList } from './utils/entities';
import * as api from '../feedback/utils/api';
import styles from './styles.module.scss';
export const CHARACTER_LIMIT = 250;
const Feedback = ({ url, pageTitle, resources }) => {
    const { register, handleSubmit, watch, setError, reset, formState: { errors, isSubmitting, isSubmitSuccessful }, } = useForm();
    const [id, setId] = useState();
    const descriptionId = useId();
    const score = watch('score');
    const hasFeedbackId = id !== undefined;
    const handleReset = () => {
        setId(undefined);
        reset();
    };
    useEffect(() => {
        if (!score) {
            return;
        }
        const handleUpdate = async () => {
            if (id) {
                updateFeedback({ id, score });
            }
        };
        const [debouncedUpdate, teardown] = debounce(handleUpdate, 1000);
        if (id) {
            debouncedUpdate();
        }
        else {
            createFeedback({ score, url: url, pageTitle: pageTitle });
        }
        return () => {
            teardown();
        };
    }, [score]);
    const createFeedback = async (data) => {
        try {
            const createFeedbackResponse = await api.createFeedback(data);
            setId(createFeedbackResponse?.id);
            track(new GittTilbakemelding({
                handling: 'gi tilbakemelding',
                objekt: 'smilefjes',
                tilbakemeldingScore: Number.parseInt(data.score, 10),
            }));
        }
        catch {
            setError('root', { message: resources.serverErrorMessage });
        }
    };
    const updateFeedback = async (data) => {
        try {
            if (id) {
                await api.updateFeedback(data);
            }
        }
        catch (e) {
            setError('root', { message: resources.serverErrorMessage });
            throw e;
        }
    };
    const getButtonAriaLabel = (value) => {
        switch (value) {
            case '5':
                return resources.veryPleasedText;
            case '4':
                return resources.slightlyPleasedText;
            case '3':
                return resources.neutralText;
            case '2':
                return resources.slightlyDispleasedText;
            case '1':
                return resources.displeasedText;
        }
    };
    const onSubmit = async (data) => {
        if (!id || id === undefined) {
            logError('Skjema for tilbakemelding fra bruker ble forsøkt sendt inn uten id.');
            setError('root', { message: resources.serverErrorMessage });
        }
        try {
            await api.updateFeedback({
                id: id,
                score: data.score,
                comment: data.comment,
            });
        }
        catch {
            setError('root', { message: resources.serverErrorMessage });
        }
    };
    if (isSubmitSuccessful || errors.root) {
        return (_jsx("div", { className: "container", children: _jsx("div", { className: "row", children: _jsxs("div", { className: "col col-md-10 col-lg-8", children: [isSubmitSuccessful && !errors.root ? (_jsx(HighlightPanel, { color: "blueberry", svgIcon: PaperPlane, children: resources.successMessage })) : (_jsx(NotificationPanel, { variant: "error", role: "alert", children: resources.serverErrorMessage })), _jsx(Spacer, { size: 'l' })] }) }) }));
    }
    return (_jsx("div", { className: styles.feedbackform, children: _jsx("div", { className: "container", children: _jsx("div", { className: "row", children: _jsx("div", { className: "col col-md-10 col-lg-8", children: _jsx("form", { onSubmit: handleSubmit(onSubmit), children: _jsxs(Validation, { errorTitle: resources.serverErrorMessage, children: [_jsx(FormGroup, { legend: resources.questionText, legendHtmlMarkup: "h2", htmlMarkup: "div", children: _jsx("div", { className: styles.feedbackform__scores, children: scoreValueList.map(value => (_jsx(ScoreInput, { label: getButtonAriaLabel(value), value: value, selected: score === value, disabled: isSubmitting, expanded: hasFeedbackId, ...register('score') }, value))) }) }), hasFeedbackId && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 'l' }), _jsx(TextArea, { error: !!errors?.comment, errorText: errors?.comment?.message, label: _jsx(Label, { labelTexts: [{ text: resources.commentLabel }], sublabel: _jsx(Sublabel, { id: descriptionId, sublabelTexts: [{ text: resources.commentDescription, type: 'subdued' }] }) }), "aria-describedby": descriptionId, maxCharacters: CHARACTER_LIMIT, resources: {
                                                characters: resources.maxText,
                                            }, disabled: isSubmitting, ...register('comment', { maxLength: { value: CHARACTER_LIMIT, message: resources.commentTooLongErrorMessage } }) })] })), _jsx(Spacer, { size: "m" }), hasFeedbackId && (_jsxs("div", { className: styles.feedbackform__buttons, children: [_jsxs(FeedbackButton, { type: "submit", disabled: isSubmitting, children: [_jsx(Icon, { svgIcon: PaperPlane, size: IconSize.XSmall }), resources.submitButtonText] }), _jsx(FeedbackButton, { variant: "borderless", onClick: handleReset, disabled: isSubmitting, type: "button", children: resources.cancelButtonText })] }))] }) }) }) }) }) }));
};
export default Feedback;
